import { formatMoney } from '@/lib/money';
import type {
  AdminModerationHistoryResponseDTO,
  AdminReportDetailDTO,
  CommentTreeDTO,
  MessageTreeDTO,
  PostDocumentResponseDTO,
} from '@/api/generated/model';
import type {
  CommentNode,
  HistoryEntry,
  MediaFile,
  ModerationDetail,
  ModerationKind,
  ModerationStatus,
  PersonInfo,
  ReportedComment,
  ReportedContent,
  ReportedMessage,
  MessageNode,
} from '../types';
import { contentTypeToKind } from '../types';
import { resolveMediaKind } from './attachments';

// Adapter: AdminReportDetailDTO (flat) -> ModerationDetail (discriminated union).
// Ports convertToModerationDetailData (ModerationDetailsViewModel.kt:64-263) 1:1. Pure + testable.

/**
 * `reportedUserStatus` is a new backend field (UserStatus code ACT/INA/TER) not yet present in the
 * orval-generated `AdminReportDetailDTO` type. Read it via a small local extension instead of editing
 * the generated model (per doc 09 §8 risk #7 convention for backend fields ahead of codegen).
 */
type AdminReportDetailDTOWithReportedUserStatus = AdminReportDetailDTO & {
  reportedUserStatus?: string | null;
};

const idStr = (n: number | null | undefined): string => (n == null ? '' : String(n));
const money = (n: number | null | undefined): string | null =>
  n == null ? null : formatMoney(n, 'USD');

/** Build a PersonInfo, or null when the identity is entirely absent. */
function buildPerson(fields: {
  id?: number | undefined;
  name?: string | undefined;
  uname?: string | undefined;
  email?: string | undefined;
  phone?: string | undefined;
  avatar?: string | undefined;
}): PersonInfo | null {
  const { id, name, uname, email, phone, avatar } = fields;
  if (id == null && !name && !uname) return null;
  return {
    name: name ?? '—',
    userId: idStr(id),
    uname: uname ?? '',
    email: email ?? '',
    phone: phone ?? '',
    avatar: avatar ?? null,
  };
}

function toReporter(dto: AdminReportDetailDTO): PersonInfo {
  return (
    buildPerson({
      id: dto.reporterUserId,
      name: dto.reporterDisplayName,
      uname: dto.reporterUName,
      email: dto.reporterEmail,
      phone: dto.reporterPhoneNumber,
      avatar: dto.reporterAvatarImage,
    }) ?? {
      name: 'Unknown',
      userId: '',
      uname: '',
      email: dto.reporterEmail ?? '',
      phone: dto.reporterPhoneNumber ?? '',
      avatar: null,
    }
  );
}

/**
 * Which person the "Reported User" resolves to depends on kind (doc 09 §2.2):
 *   USER -> reportedUser* (id falls back to contentId); CONTENT -> postCreator*;
 *   COMMENT -> reportedCommentAuthor*; MESSAGE -> reportedMessageAuthor*; FEEDBACK -> none.
 */
function resolveReportedUser(dto: AdminReportDetailDTO, kind: ModerationKind): PersonInfo | null {
  switch (kind) {
    case 'USER':
      return buildPerson({
        id: dto.reportedUserId ?? dto.contentId,
        name: dto.reportedUserDisplayName,
        uname: dto.reportedUserUName,
        email: dto.reportedUserEmail,
        phone: dto.reportedUserPhoneNumber,
        avatar: dto.reportedUserAvatarImage,
      });
    case 'CONTENT':
      return buildPerson({
        id: dto.postCreatorId,
        name: dto.postCreatorDisplayName,
        uname: dto.postCreatorUName,
        email: dto.postCreatorEmail,
        phone: dto.postCreatorPhoneNumber,
        avatar: dto.postCreatorAvatarImage,
      });
    case 'COMMENT':
      return buildPerson({
        id: dto.reportedCommentAuthorId,
        name: dto.reportedCommentAuthorDisplayName,
        uname: dto.reportedCommentAuthorUName,
        email: dto.reportedCommentAuthorEmail,
        phone: dto.reportedCommentAuthorPhoneNumber,
        avatar: dto.reportedCommentAuthorAvatarImage,
      });
    case 'MESSAGE':
      return buildPerson({
        id: dto.reportedMessageAuthorId,
        name: dto.reportedMessageAuthorDisplayName,
        uname: dto.reportedMessageAuthorUName,
        email: dto.reportedMessageAuthorEmail,
        phone: dto.reportedMessageAuthorPhoneNumber,
        avatar: dto.reportedMessageAuthorAvatarImage,
      });
    case 'FEEDBACK':
    default:
      return null;
  }
}

function toMediaFile(doc: PostDocumentResponseDTO): MediaFile {
  return {
    id: idStr(doc.id),
    fileUrl: doc.fileUrl ?? null,
    thumbnail: doc.thumbnail ?? null,
    playbackId: doc.playbackId ?? null,
    kind: resolveMediaKind(doc.fileType, doc.fileUrl),
  };
}

function toReportedContent(dto: AdminReportDetailDTO): ReportedContent {
  return {
    id: idStr(dto.contentId),
    title: dto.postContent ?? '—',
    creator: dto.postCreatorDisplayName ?? '—',
    creatorUserId: idStr(dto.postCreatorId),
    type: dto.postType ?? '—',
    status: dto.postStatus ?? '—',
    uploadDate: dto.postCreatedAt ?? '',
    price: money(dto.postPrice) ?? '—',
    country: dto.postLocation ?? '—',
    ip: '—', // not exposed by the DTO (parity: CMP renders "N/A")
    device: '—', // not exposed by the DTO
    collaborator: dto.collaboratorDisplayName ?? null,
    hashtags: dto.hashtags ?? null,
    thumbnail: dto.postThumbnail ?? null,
    mediaFiles: (dto.postDocuments ?? []).map(toMediaFile),
    totals: {
      like: dto.postTotalLike ?? 0,
      comment: dto.postTotalComment ?? 0,
      view: dto.postTotalView ?? 0,
      share: dto.postTotalShare ?? 0,
      bookmark: dto.postTotalBookmark ?? 0,
    },
    totalEarned: money(dto.postTotalEarned),
    totalTips: money(dto.postTotalTips),
    releaseTime: dto.postReleaseTime ?? null,
    expirationTime: dto.postExpirationTime ?? null,
  };
}

/** Recursively map CommentTreeDTO -> CommentNode (parity convertCommentTreeToNode :465-480). */
function convertCommentTree(node: CommentTreeDTO): CommentNode {
  return {
    id: idStr(node.id),
    text: node.content ?? '',
    author: node.authorDisplayName ?? '—',
    authorUserId: idStr(node.authorId),
    authorUname: node.authorUName ?? '',
    date: node.createdAt ?? '',
    likes: node.totalLike ?? 0,
    avatar: node.authorAvatarImage ?? null,
    isReported: node.isReported === true,
    children: (node.childComments ?? []).map(convertCommentTree),
    replyToUname: node.replyToUName ?? null,
  };
}

function toReportedComment(dto: AdminReportDetailDTO): ReportedComment {
  return {
    tree: dto.commentTree ? convertCommentTree(dto.commentTree) : null,
    reportedCommentId: dto.reportedCommentId == null ? null : String(dto.reportedCommentId),
    postTitle: dto.commentPostContent ?? '—',
    postId: idStr(dto.commentPostId),
    platform: 'TipMi',
  };
}

function toMessageNode(node: MessageTreeDTO): MessageNode {
  return {
    id: node.messageId ?? '',
    text: node.content ?? '',
    author: node.authorDisplayName ?? '—',
    authorUserId: idStr(node.authorId),
    authorUname: node.authorUName ?? '',
    avatar: node.authorAvatarImage ?? null,
    createdAt: node.createdAt ?? null,
    contentType: node.contentType ?? node.messageType ?? null,
    postId: node.postId ?? null,
    isReported: node.isReported === true,
  };
}

function toReportedMessage(dto: AdminReportDetailDTO): ReportedMessage {
  return {
    messages: (dto.messageTree ?? []).map(toMessageNode),
    platform: 'TipMi',
  };
}

/**
 * Merged timeline (parity :310-337): always prepend a synthetic "Report Submitted" head (by System,
 * using reportedAt + description), then the server history list.
 */
function buildHistory(
  dto: AdminReportDetailDTO,
  history: AdminModerationHistoryResponseDTO[],
): HistoryEntry[] {
  const head: HistoryEntry = {
    action: 'Report Submitted',
    date: dto.reportedAt ?? '',
    moderator: 'System',
    moderatorRole: null,
    description: dto.description ?? '',
  };
  const rest: HistoryEntry[] = history.map((h) => ({
    action: h.action ?? '—',
    date: h.createdAt ?? '',
    moderator: h.moderatorName?.trim() || 'System',
    moderatorRole: h.moderatorRole ?? null,
    description: h.description ?? '',
  }));
  return [head, ...rest];
}

export function toModerationDetail(
  dto: AdminReportDetailDTO,
  history: AdminModerationHistoryResponseDTO[],
): ModerationDetail {
  const kind = contentTypeToKind(dto.contentType);
  const status: ModerationStatus =
    (dto.reportStatus ?? '').toUpperCase() === 'COMPLETED' ? 'DISMISSED' : 'PENDING';

  const base = {
    id: idStr(dto.id),
    reportId: `REP-${dto.id ?? ''}`,
    contentId: dto.contentId == null ? null : String(dto.contentId),
    reason: dto.category ?? dto.userReportType ?? 'Unknown',
    description: dto.description ?? '',
    useCase: dto.useCase ?? null,
    status,
    priority: 'HIGH' as const, // CMP hardcodes HIGH (doc 09 §2.2, risk #6)
    reportedDate: dto.reportedAt ?? '',
    reporter: toReporter(dto),
    reportedUser: resolveReportedUser(dto, kind),
    reportedUserStatus:
      (dto as AdminReportDetailDTOWithReportedUserStatus).reportedUserStatus ?? null,
    history: buildHistory(dto, history),
  };

  switch (kind) {
    case 'USER':
      return {
        ...base,
        kind,
        user: {
          pricePerMonth: money(dto.pricePerMonth),
          registeredAt: dto.registeredAt ?? null,
          lastLogin: dto.lastLogin ?? null,
        },
      };
    case 'FEEDBACK':
      // attach_files_urls is kept snake_case in the generated model (doc 09 §8 risk #7).
      return { ...base, kind, attachments: dto.attach_files_urls ?? [] };
    case 'COMMENT':
      return { ...base, kind, comment: toReportedComment(dto) };
    case 'MESSAGE':
      return { ...base, kind, message: toReportedMessage(dto) };
    case 'CONTENT':
    default:
      return { ...base, kind: 'CONTENT', content: toReportedContent(dto) };
  }
}
